import { api, tokenStore } from './client';

// ============= TYPES =============
export interface User {
  id: string;
  email: string;
  full_name: string | null;
  locale: string;
  timezone: string;
  created_at: string;
}

export interface AuthTokens { accessToken: string; refreshToken: string }

export interface EmailAccount {
  id: string;
  email_address: string;
  display_name: string | null;
  imap_host: string;
  smtp_host: string;
  sync_status: 'idle' | 'syncing' | 'error' | 'paused';
  sync_error: string | null;
  last_incremental_sync_at: string | null;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  // condivisione/delega
  shared?: boolean;
  shared_by?: string | null;
  can_send?: boolean;
}

export interface AccountShare {
  id: string;
  can_send: boolean;
  created_at: string;
  delegate_email: string;
  delegate_name: string | null;
}
export const sharesApi = {
  list: (accountId: string) => api.get<{ shares: AccountShare[] }>(`/accounts/${accountId}/shares`),
  create: (accountId: string, email: string, can_send: boolean) =>
    api.post<{ share: AccountShare }>(`/accounts/${accountId}/shares`, { email, can_send }),
  remove: (accountId: string, shareId: string) =>
    api.delete<{ ok: true }>(`/accounts/${accountId}/shares/${shareId}`),
};

export interface DiscoverConfig {
  imap: { host: string; port: number; secure: boolean };
  smtp: { host: string; port: number; secure: boolean };
  source: 'hardcoded' | 'convention';
}

export interface Folder {
  id: string;
  path: string;
  name: string;
  role: string | null;
  total_count: number;
  unread_count: number;
}

export type FolderRole = 'inbox' | 'sent' | 'drafts' | 'trash' | 'spam' | 'archive';

export interface MessageListItem {
  id: string;
  uid: number;
  subject: string;
  snippet: string;
  from_address: { name: string; email: string } | null;
  to_addresses: { name: string; email: string }[];
  is_read: boolean;
  is_starred: boolean;
  has_attachments: boolean;
  received_at: string | null;
  thread_id: string;
  message_count: number;
  ai_category: string | null;
  ai_urgency: 'low' | 'medium' | 'high' | 'critical' | null;
  snoozed_until: string | null;
  is_archived: boolean;
  thread_starred: boolean;
  labels: string[];
}

export interface MessageDetail extends MessageListItem {
  body_text: string | null;
  body_html: string | null;
  cc_addresses: { name: string; email: string }[];
  in_reply_to: string | null;
  message_id: string | null;
  folder_path: string;
  account_id: string;
}

export interface ThreadDetail {
  id: string;
  subject: string;
  ai_category: string | null;
  ai_urgency: string | null;
  ai_summary: string | null;
  ai_action_items: string[] | null;
  message_count: number;
  participants: { name?: string; email?: string }[];
  is_starred: boolean;
  is_archived: boolean;
  snoozed_until: string | null;
  labels: string[];
}

export interface AttachmentItem {
  id: string;
  filename: string;
  contentType: string | null;
  sizeBytes: number;
  cached: boolean;
}

export interface Draft {
  id: string;
  account_id: string;
  in_reply_to_message_id: string | null;
  forward_of_message_id: string | null;
  thread_id: string | null;
  subject: string;
  to_addresses: { name?: string; address: string }[];
  cc_addresses: { name?: string; address: string }[];
  bcc_addresses: { name?: string; address: string }[];
  body_text: string;
  body_html: string;
  scheduled_send_at: string | null;
  sent_at: string | null;
  send_error: string | null;
  updated_at: string;
}

export interface DraftAttachment {
  id: string;
  filename: string;
  content_type: string | null;
  size_bytes: number;
}

export interface Reminder {
  id: string;
  thread_id: string;
  message_id: string | null;
  remind_at: string;
  reason: string | null;
  triggered_at: string | null;
  dismissed_at: string | null;
  subject: string;
}

export interface UserSettings {
  signature_html: string;
  signature_text: string;
  default_reply_tone: 'formale' | 'amichevole' | 'breve' | 'dettagliato';
  theme: 'system' | 'light' | 'dark';
  notifications_enabled: boolean;
  keyboard_shortcuts_enabled: boolean;
  auto_save_draft_seconds: number;
  inbox_zero_summary_time: string;
}

// ============= AUTH =============
export const authApi = {
  register: (data: { email: string; password: string; fullName?: string }) =>
    api.post<{ user: User }>('/auth/register', data),
  login: async (data: { email: string; password: string }) => {
    const result = await api.post<{ user: User; tokens: AuthTokens }>('/auth/login', data);
    tokenStore.set(result.tokens.accessToken, result.tokens.refreshToken);
    return result;
  },
  logout: async () => {
    const refresh = tokenStore.getRefresh();
    if (refresh) await api.post('/auth/logout', { refreshToken: refresh }).catch(() => {});
    tokenStore.clear();
  },
  me: () => api.get<{ user: User }>('/auth/me'),
  forgotPassword: (email: string) => api.post<{ ok: true }>('/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string) =>
    api.post<{ ok: true }>('/auth/reset-password', { token, password }),
};

// ============= ACCOUNTS =============
export const accountsApi = {
  list: () => api.get<{ accounts: EmailAccount[] }>('/accounts'),
  discover: (emailAddress: string) =>
    api.post<{ config: DiscoverConfig }>('/accounts/discover', { emailAddress }),
  add: (data: {
    emailAddress: string;
    password: string;
    displayName?: string;
    imapHost?: string;
    imapPort?: number;
    smtpHost?: string;
    smtpPort?: number;
    imapTlsRejectUnauthorized?: boolean;
    smtpTlsRejectUnauthorized?: boolean;
  }) => api.post<{ accountId: string }>('/accounts', data),
  remove: (id: string) => api.delete<{ ok: true }>(`/accounts/${id}`),
  forceSync: (id: string) =>
    api.post<{ ok: true; status: string }>(`/accounts/${id}/sync`),
  folders: (id: string) =>
    api.get<{ folders: Folder[] }>(`/accounts/${id}/folders`),
};

// ============= MESSAGES =============
export const messagesApi = {
  list: (params: {
    accountId: string;
    folderRole?: FolderRole;
    folder?: string;
    limit?: number;
    cursor?: string;
    category?: string;
    label?: string;
    urgency?: string;
    unreadOnly?: boolean;
    starredOnly?: boolean;
    hideSnoozed?: boolean;
    hideArchived?: boolean;
  }) => {
    const qs = new URLSearchParams();
    qs.set('accountId', params.accountId);
    if (params.folderRole) qs.set('folderRole', params.folderRole);
    if (params.folder) qs.set('folder', params.folder);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.cursor) qs.set('cursor', params.cursor);
    if (params.category) qs.set('category', params.category);
    if (params.label) qs.set('label', params.label);
    if (params.urgency) qs.set('urgency', params.urgency);
    if (params.unreadOnly) qs.set('unreadOnly', 'true');
    if (params.starredOnly) qs.set('starredOnly', 'true');
    if (params.hideSnoozed === false) qs.set('hideSnoozed', 'false');
    if (params.hideArchived === false) qs.set('hideArchived', 'false');
    return api.get<{ messages: MessageListItem[]; nextCursor: string | null }>(
      `/messages?${qs.toString()}`
    );
  },
  detail: (id: string) => api.get<{ message: MessageDetail }>(`/messages/${id}`),
  thread: (id: string) =>
    api.get<{ thread: ThreadDetail; messages: MessageDetail[] }>(`/threads/${id}`),
  markRead: (id: string, read: boolean) =>
    api.post<{ ok: true }>(`/messages/${id}/read`, { read }),
  star: (id: string, starred: boolean) =>
    api.post<{ ok: true }>(`/messages/${id}/star`, { starred }),
};

// ============= THREADS ACTIONS =============
export const threadsApi = {
  archive: (id: string) => api.post<{ ok: true }>(`/threads/${id}/archive`),
  trash: (id: string) => api.post<{ ok: true }>(`/threads/${id}/trash`),
  move: (id: string, targetFolder: string) =>
    api.post<{ ok: true; moved: number }>(`/threads/${id}/move`, { targetFolder }),
  spam: (id: string, blockSender = true) =>
    api.post<{ ok: true; blocked: boolean; sender: string | null }>(`/threads/${id}/spam`, { blockSender }),
  snooze: (id: string, until: string) =>
    api.post<{ ok: true }>(`/threads/${id}/snooze`, { until }),
  unsnooze: (id: string) => api.post<{ ok: true }>(`/threads/${id}/unsnooze`),
  star: (id: string, starred: boolean) =>
    api.post<{ ok: true }>(`/threads/${id}/star`, { starred }),
  setLabels: (id: string, labels: string[]) =>
    api.post<{ ok: true }>(`/threads/${id}/labels`, { labels }),
  markRead: (id: string, read: boolean) =>
    api.post<{ ok: true }>(`/threads/${id}/read`, { read }),
};

export type SignatureStyle = 'minimal' | 'corporate' | 'creative' | 'tech';
export const signatureApi = {
  generate: (data: {
    full_name: string;
    role?: string;
    company?: string;
    phone?: string;
    mobile?: string;
    email?: string;
    website?: string;
    address?: string;
    linkedin?: string;
    logo_url?: string;
    style: SignatureStyle;
  }) => api.post<{ html: string; plain_text: string }>('/ai/signature/generate', data),
};

// ============= DRAFTS =============
export const draftsApi = {
  list: () => api.get<{ drafts: Draft[] }>('/drafts'),
  detail: (id: string) =>
    api.get<{ draft: Draft; attachments: DraftAttachment[] }>(`/drafts/${id}`),
  create: (data: Partial<Draft> & { accountId: string }) =>
    api.post<{ draftId: string }>('/drafts', data),
  update: (id: string, data: Partial<Draft>) =>
    api.patch<{ ok: true }>(`/drafts/${id}`, data),
  remove: (id: string) => api.delete<{ ok: true }>(`/drafts/${id}`),
  uploadAttachment: (id: string, data: { filename: string; contentType?: string; base64: string }) =>
    api.post<{ attachmentId: string; sizeBytes: number }>(`/drafts/${id}/attachments`, data),
  removeAttachment: (id: string, attachmentId: string) =>
    api.delete<{ ok: true }>(`/drafts/${id}/attachments/${attachmentId}`),
  send: (id: string, scheduledSendAt?: string) =>
    api.post<{ ok: true; scheduled?: boolean }>(`/drafts/${id}/send`, scheduledSendAt ? { scheduledSendAt } : {}),
};

// ============= ATTACHMENTS =============
export const attachmentsApi = {
  list: (messageId: string) =>
    api.get<{ attachments: AttachmentItem[] }>(`/messages/${messageId}/attachments`),
  downloadUrl: (id: string) => `/api/v1/attachments/${id}/download`,
};

// ============= REMINDERS =============
export const remindersApi = {
  list: () => api.get<{ reminders: Reminder[] }>('/reminders'),
  create: (data: { threadId: string; messageId?: string; remindAt: string; reason?: string }) =>
    api.post<{ reminderId: string }>('/reminders', data),
  dismiss: (id: string) => api.post<{ ok: true }>(`/reminders/${id}/dismiss`),
  remove: (id: string) => api.delete<{ ok: true }>(`/reminders/${id}`),
};

// ============= SETTINGS =============
export const settingsApi = {
  get: () => api.get<{ settings: UserSettings }>('/settings'),
  update: (data: Partial<UserSettings>) =>
    api.patch<{ ok: true }>('/settings', {
      signatureHtml: data.signature_html,
      signatureText: data.signature_text,
      defaultReplyTone: data.default_reply_tone,
      theme: data.theme,
      notificationsEnabled: data.notifications_enabled,
      keyboardShortcutsEnabled: data.keyboard_shortcuts_enabled,
      autoSaveDraftSeconds: data.auto_save_draft_seconds,
      inboxZeroSummaryTime: data.inbox_zero_summary_time,
    }),
};

// ============= AI =============
export const aiApi = {
  smartReply: (data: {
    threadId: string;
    tone?: 'formale' | 'amichevole' | 'breve' | 'dettagliato';
    instructions?: string;
  }) => api.post<{ draftId: string; body: string }>('/ai/smart-reply', data),
  summarizeThread: (threadId: string) =>
    api.post<{ summary: string }>(`/ai/summarize-thread/${threadId}`),
  search: (data: { query: string; accountId?: string; limit?: number; synthesize?: boolean }) =>
    api.post<{ results: MessageListItem[]; synthesis: string | null }>('/ai/search', data),
  compose: (data: { intent: string; tone?: 'formale' | 'amichevole' | 'breve' | 'dettagliato'; to?: string }) =>
    api.post<{ subject: string; body: string }>('/ai/compose', data),
  contactSummary: (email: string) =>
    api.post<{ summary: string; count: number }>('/ai/contact-summary', { email }),
};

export interface FollowUp {
  thread_id: string;
  message_id: string;
  account_id: string;
  subject: string;
  to: string;
  to_name: string | null;
  last_sent_at: string;
  days_waiting: number;
}
export const followUpsApi = {
  list: (days = 3) => api.get<{ followUps: FollowUp[] }>(`/follow-ups?days=${days}`),
};

// ============= SEARCH =============
export const searchApi = {
  keyword: (q: string, accountId?: string) => {
    const qs = new URLSearchParams({ q });
    if (accountId) qs.set('accountId', accountId);
    return api.get<{ messages: MessageListItem[] }>(`/search?${qs.toString()}`);
  },
};

// ============= CONTACTS (Rubrica) =============
export interface Contact {
  id: string;
  account_id: string;
  email: string;
  name: string | null;
  company: string | null;
  role: string | null;
  phone: string | null;
  notes: string | null;
  is_favorite: boolean;
  tags: string[];
  source: string;
  interaction_count: number;
  last_interaction_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContactInput {
  email: string;
  name?: string | null;
  company?: string | null;
  role?: string | null;
  phone?: string | null;
  notes?: string | null;
  tags?: string[];
  is_favorite?: boolean;
}

// ============= LABELS (Etichette) =============
export interface Label { id: string; name: string; color: string; created_at: string }
export const labelsApi = {
  list: () => api.get<{ labels: Label[] }>('/labels'),
  create: (name: string, color?: string) => api.post<{ label: Label }>('/labels', { name, color }),
  update: (id: string, data: { name?: string; color?: string }) => api.patch<{ label: Label }>(`/labels/${id}`, data),
  remove: (id: string) => api.delete<{ ok: true }>(`/labels/${id}`),
};

// ============= CALENDARIO =============
export interface CalendarEvent {
  id: string;
  title: string;
  description: string | null;
  location: string | null;
  start_at: string;
  end_at: string;
  all_day: boolean;
  color: string;
  source: string;
  source_message_id: string | null;
}
export interface CalendarEventInput {
  title: string;
  description?: string;
  location?: string;
  start_at: string;
  end_at: string;
  all_day?: boolean;
  color?: string;
}
export const calendarApi = {
  range: (from: string, to: string) =>
    api.get<{ events: CalendarEvent[] }>(`/calendar/events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  create: (data: CalendarEventInput) => api.post<{ event: CalendarEvent }>('/calendar/events', data),
  update: (id: string, data: Partial<CalendarEventInput>) => api.patch<{ event: CalendarEvent }>(`/calendar/events/${id}`, data),
  remove: (id: string) => api.delete<{ ok: true }>(`/calendar/events/${id}`),
  // Google Calendar (sync bidirezionale)
  googleStatus: () => api.get<{ connected: boolean }>('/ai/calendar/google/status'),
  googleAuthUrl: () => api.get<{ auth_url: string }>('/ai/calendar/google/auth-url'),
  googleDisconnect: () => api.post<{ ok: true }>('/ai/calendar/google/disconnect'),
  googleSync: () => api.post<{ connected: boolean; pushed: number; pulled: number; deleted: number }>('/calendar/google/sync'),
  // Scadenzario AI: suggerimenti di eventi estratti dalle email
  pending: () => api.get<{ events: CalendarSuggestion[] }>('/ai/calendar/pending'),
  scan: () => api.post<{ scanned: number; found: number }>('/ai/calendar/scan', {}),
  confirmSuggestion: (eventId: string) => api.post<{ ok: true; eventId: string }>('/ai/calendar/confirm', { event_id: eventId }),
  dismissSuggestion: (eventId: string) => api.post<{ ok: true }>('/ai/calendar/dismiss', { event_id: eventId }),
};

export interface ExtractedDocument {
  id: string;
  message_id: string;
  thread_id: string | null;
  doc_type: string;
  doc_number: string | null;
  amount: string | null;
  currency: string | null;
  doc_date: string | null;
  due_date: string | null;
  counterpart: string | null;
  subject: string | null;
  created_at: string;
}
export const documentsApi = {
  list: (type?: string) => api.get<{ documents: ExtractedDocument[] }>(`/documents${type ? `?type=${encodeURIComponent(type)}` : ''}`),
  scan: () => api.post<{ scanned: number; found: number }>('/documents/scan', {}),
  remove: (id: string) => api.delete<{ ok: true }>(`/documents/${id}`),
};

export interface CalendarSuggestion {
  id: string;
  message_id: string;
  title: string;
  start_iso: string;
  end_iso: string;
  location: string | null;
  description: string | null;
  confidence: number;
}

// ============= RISPOSTE AI (proattive) =============
export interface AiReplySuggestion {
  id: string;
  reply_text: string;
  created_at: string;
  account_id: string;
  thread_id: string;
  message_id: string;
  subject: string | null;
  from_address: { name?: string; email?: string } | null;
  snippet: string | null;
  received_at: string | null;
  account_email: string;
}
export const aiRepliesApi = {
  list: () => api.get<{ suggestions: AiReplySuggestion[] }>('/ai/replies'),
  generate: () => api.post<{ created: number }>('/ai/replies/generate', {}),
  update: (id: string, reply_text: string) => api.patch<{ ok: true }>(`/ai/replies/${id}`, { reply_text }),
  dismiss: (id: string) => api.post<{ ok: true }>(`/ai/replies/${id}/dismiss`, {}),
  send: (id: string, reply_text?: string) =>
    api.post<{ ok: true }>(`/ai/replies/${id}/send`, reply_text ? { reply_text } : {}),
};

// ============= INBOX ZERO =============
export type InboxZeroCategory = 'to_reply' | 'to_archive' | 'to_delete' | 'newsletter' | 'promo';
export type InboxZeroAction = 'archive' | 'delete' | 'mark_read';
export interface InboxZeroCategorized {
  message_id: string;
  category: InboxZeroCategory;
  reason: string;
  confidence: number;
  subject: string;
  from: string;
  received_at: string;
}
export interface InboxZeroRun {
  id: string;
  account_id: string;
  started_at: string | null;
  completed_at: string | null;
  messages_total: number;
  messages_archived: number;
  messages_deleted: number;
  messages_skipped: number;
}
export const inboxZeroApi = {
  preview: (accountId: string, limit = 20) =>
    api.get<{ messages: unknown[]; categorized: InboxZeroCategorized[] }>(
      `/ai/inbox-zero/preview?account_id=${accountId}&limit=${limit}`
    ),
  apply: (accountId: string, actions: { message_id: string; action: InboxZeroAction }[]) =>
    api.post<{ ok: true; archived: number; deleted: number; marked: number }>(
      '/ai/inbox-zero/apply',
      { account_id: accountId, actions }
    ),
  history: () => api.get<{ runs: InboxZeroRun[] }>('/ai/inbox-zero/history'),
};

// ============= RULES (Filtri/Regole) =============
export type RuleField = 'from' | 'to' | 'subject';
export type RuleOp = 'contains' | 'not_contains' | 'equals' | 'starts_with' | 'ends_with';
export interface RuleCondition { field: RuleField; op: RuleOp; value: string }
export interface RuleConditions { match: 'all' | 'any'; conditions: RuleCondition[] }
export type RuleActionType = 'mark_read' | 'star' | 'set_category' | 'archive' | 'trash';
export interface RuleAction { type: RuleActionType; value?: string }
export interface Rule {
  id: string;
  account_id: string | null;
  name: string;
  is_active: boolean;
  priority: number;
  conditions: RuleConditions;
  actions: RuleAction[];
  created_at: string;
  updated_at: string;
}
export interface RuleInput {
  name: string;
  account_id?: string | null;
  is_active?: boolean;
  priority?: number;
  conditions: RuleConditions;
  actions: RuleAction[];
}
export const rulesApi = {
  list: () => api.get<{ rules: Rule[] }>('/rules'),
  create: (data: RuleInput) => api.post<{ rule: Rule }>('/rules', data),
  update: (id: string, data: Partial<RuleInput>) => api.patch<{ rule: Rule }>(`/rules/${id}`, data),
  remove: (id: string) => api.delete<{ ok: true }>(`/rules/${id}`),
};

// ============= AUTORESPONDER (Out-of-office) =============
export interface Autoresponder {
  account_id: string;
  enabled: boolean;
  subject: string;
  body_text: string;
  body_html: string | null;
  start_at: string | null;
  end_at: string | null;
}

export const autoresponderApi = {
  get: (accountId: string) =>
    api.get<{ autoresponder: Autoresponder }>(`/accounts/${accountId}/autoresponder`),
  update: (accountId: string, data: Partial<Omit<Autoresponder, 'account_id'>>) =>
    api.put<{ autoresponder: Autoresponder }>(`/accounts/${accountId}/autoresponder`, data),
  generate: (data: { fullName?: string; company?: string; fromDate?: string; toDate?: string; reason?: string; altContact?: string }) =>
    api.post<{ subject: string; body_text: string }>(`/ai/autoresponder/generate`, data),
};

export const contactsApi = {
  list: (params: { search?: string; favoritesOnly?: boolean; accountId?: string; limit?: number; offset?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.search) qs.set('search', params.search);
    if (params.favoritesOnly) qs.set('favoritesOnly', 'true');
    if (params.accountId) qs.set('accountId', params.accountId);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.offset) qs.set('offset', String(params.offset));
    return api.get<{ contacts: Contact[]; total: number }>(`/contacts?${qs.toString()}`);
  },
  suggest: (q: string) =>
    api.get<{ contacts: Contact[] }>(`/contacts/suggest?q=${encodeURIComponent(q)}`),
  get: (id: string) => api.get<{ contact: Contact }>(`/contacts/${id}`),
  create: (accountId: string, data: ContactInput) =>
    api.post<{ contact: Contact }>('/contacts', { accountId, ...data }),
  update: (id: string, data: Partial<ContactInput>) =>
    api.patch<{ contact: Contact }>(`/contacts/${id}`, data),
  remove: (id: string) => api.delete<{ ok: true }>(`/contacts/${id}`),
  import: (accountId: string) =>
    api.post<{ ok: true; imported: number }>('/contacts/import', { accountId }),
  enrich: (id: string) => api.post<{ contact: Contact }>(`/contacts/${id}/enrich`),
};
